import { type FormEvent, useState } from 'react';
import { useApi } from '@plataforma/web/api';
import { CampoImagen } from '../componentes/Imagenes';
import type { IdentidadTienda as Identidad, Sitio } from './comercial-comun';

const SENALES: [NonNullable<Identidad['senales']>[number], string][] = [
  ['pedimento', 'Importamos con pedimento'],
  ['historial', 'Documentamos cada reparación'],
  ['garantia', 'Damos garantía por escrito'],
  ['envios', 'Enviamos a todo México'],
  ['facturacion', 'Facturamos'],
];
const REDES: [keyof NonNullable<Identidad['redes']>, string, string][] = [
  ['facebook', 'Facebook', 'https://www.facebook.com/tu-negocio'],
  ['instagram', 'Instagram', 'https://www.instagram.com/tu-negocio'],
  ['tiktok', 'TikTok', 'https://www.tiktok.com/@tu-negocio'],
  ['youtube', 'YouTube', 'https://www.youtube.com/@tu-negocio'],
  ['mercadoLibre', 'Mercado Libre', 'https://www.mercadolibre.com.mx/…'],
];

type Servicio = NonNullable<Identidad['servicios']>[number];
type Cifra = NonNullable<Identidad['cifras']>[number];
type Persona = NonNullable<Identidad['equipo']>[number];

/** Quita los campos vacíos: el API rechaza textos vacíos y así lo opcional queda opcional. */
const limpio = <T extends object>(o: T) =>
  Object.fromEntries(
    Object.entries(o).filter(([, v]) => v !== '' && v !== undefined && v !== null),
  ) as T;

/**
 * Identidad de la tienda (arquitectura 2.19.2): lo que las plantillas muestran aunque todavía no
 * haya inventario. Se guarda aparte de los datos básicos (PUT /commerce/sitio/identidad).
 */
export function IdentidadTienda({ sitio, alGuardar }: { sitio: Sitio; alGuardar: () => void }) {
  const api = useApi();
  const i = sitio.identidad ?? {};
  const [servicios, setServicios] = useState<Servicio[]>(i.servicios ?? []);
  const [cifras, setCifras] = useState<Cifra[]>(i.cifras ?? []);
  const [equipo, setEquipo] = useState<Persona[]>(i.equipo ?? []);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const guardar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    const t = (k: string) => String(d.get(k) ?? '').trim();
    const redes = limpio(Object.fromEntries(REDES.map(([k]) => [k, t(`red-${k}`)])));
    setError(null);
    setAviso(null);
    try {
      await api(
        'PUT',
        '/commerce/sitio/identidad',
        limpio({
          quienesSomos: t('quienesSomos'),
          historia: t('historia'),
          firma: t('firma'),
          fundadoEn: t('fundadoEn') ? Number(t('fundadoEn')) : undefined,
          garantia: t('garantia'),
          senales: SENALES.map(([k]) => k).filter((k) => d.get(`senal-${k}`) === 'on'),
          servicios: servicios.map(limpio).filter((s) => s.titulo),
          cifras: cifras.filter((c) => c.valor && c.etiqueta),
          equipo: equipo
            .map((p, n) => limpio({ ...p, foto: t(`foto-${n}`) || undefined }))
            .filter((p) => p.nombre),
          ...(Object.keys(redes).length && { redes }),
        }),
      );
      setAviso('Identidad guardada');
      alGuardar();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const fila = <T,>(lista: T[], poner: (l: T[]) => void, n: number, cambio: Partial<T>) =>
    poner(lista.map((x, k) => (k === n ? { ...x, ...cambio } : x)));

  return (
    <form id="identidad" className="seccion cuerpo formulario" onSubmit={(e) => void guardar(e)}>
      <h2>Identidad de tu tienda</h2>
      <p className="subtitulo">
        Esto es lo que tu tienda muestra aunque todavía no tengas autos o piezas publicados: quién
        eres, qué haces y por qué confiar en ti.
      </p>
      {error && <p className="error">{error}</p>}
      {aviso && <p className="etiqueta exito">{aviso}</p>}
      <label className="campo">
        <span>Quiénes somos</span>
        <textarea
          name="quienesSomos"
          defaultValue={i.quienesSomos ?? ''}
          rows={3}
          maxLength={600}
        />
      </label>
      <label className="campo">
        <span>Tu historia</span>
        <textarea name="historia" defaultValue={i.historia ?? ''} rows={5} maxLength={3000} />
        <small className="subtitulo">
          En primera persona funciona mejor: cómo empezaste y cómo trabajas.
        </small>
      </label>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <label className="campo" style={{ flex: 2 }}>
          <span>Quién la firma</span>
          <input
            name="firma"
            defaultValue={i.firma ?? ''}
            maxLength={80}
            placeholder="Nombre, cargo"
          />
        </label>
        <label className="campo" style={{ flex: 1 }}>
          <span>Año en que abriste</span>
          <input
            name="fundadoEn"
            type="number"
            min={1950}
            max={new Date().getFullYear()}
            defaultValue={i.fundadoEn ?? ''}
          />
        </label>
      </div>

      <fieldset className="campo">
        <legend>Servicios o especialidades</legend>
        {servicios.map((s, n) => (
          <div key={n} style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              aria-label="Servicio"
              placeholder="Reparación de siniestrados"
              value={s.titulo}
              maxLength={60}
              onChange={(e) => fila(servicios, setServicios, n, { titulo: e.target.value })}
              style={{ flex: 2 }}
            />
            <input
              aria-label="Descripción"
              placeholder="Hojalatería, pintura y mecánica"
              value={s.descripcion ?? ''}
              maxLength={240}
              onChange={(e) => fila(servicios, setServicios, n, { descripcion: e.target.value })}
              style={{ flex: 3 }}
            />
            <input
              aria-label="Precio desde"
              placeholder="Desde $189,000"
              value={s.precioDesde ?? ''}
              maxLength={40}
              onChange={(e) => fila(servicios, setServicios, n, { precioDesde: e.target.value })}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="boton"
              onClick={() => setServicios(servicios.filter((_, k) => k !== n))}
            >
              Quitar
            </button>
          </div>
        ))}
        {servicios.length < 8 && (
          <button
            type="button"
            className="boton"
            onClick={() => setServicios([...servicios, { titulo: '' }])}
          >
            Agregar servicio
          </button>
        )}
      </fieldset>

      <fieldset className="campo">
        <legend>Tus cifras (hasta 4)</legend>
        {cifras.map((c, n) => (
          <div key={n} style={{ display: 'flex', gap: 8 }}>
            <input
              aria-label="Cifra"
              placeholder="+120"
              value={c.valor}
              maxLength={12}
              onChange={(e) => fila(cifras, setCifras, n, { valor: e.target.value })}
              style={{ width: 110 }}
            />
            <input
              aria-label="Qué significa"
              placeholder="autos reparados y entregados"
              value={c.etiqueta}
              maxLength={60}
              onChange={(e) => fila(cifras, setCifras, n, { etiqueta: e.target.value })}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              className="boton"
              onClick={() => setCifras(cifras.filter((_, k) => k !== n))}
            >
              Quitar
            </button>
          </div>
        ))}
        {cifras.length < 4 && (
          <button
            type="button"
            className="boton"
            onClick={() => setCifras([...cifras, { valor: '', etiqueta: '' }])}
          >
            Agregar cifra
          </button>
        )}
      </fieldset>

      <fieldset className="campo">
        <legend>Tu equipo</legend>
        {equipo.map((p, n) => (
          <div key={n} className="seccion" style={{ display: 'grid', gap: 8, padding: 12 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input
                aria-label="Nombre"
                placeholder="Toño"
                value={p.nombre}
                maxLength={40}
                onChange={(e) => fila(equipo, setEquipo, n, { nombre: e.target.value })}
              />
              <input
                aria-label="Puesto"
                placeholder="Hojalatería"
                value={p.puesto ?? ''}
                maxLength={60}
                onChange={(e) => fila(equipo, setEquipo, n, { puesto: e.target.value })}
              />
              <input
                aria-label="Una frase"
                placeholder="22 años con el martillo"
                value={p.nota ?? ''}
                maxLength={80}
                onChange={(e) => fila(equipo, setEquipo, n, { nota: e.target.value })}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="boton"
                onClick={() => setEquipo(equipo.filter((_, k) => k !== n))}
              >
                Quitar
              </button>
            </div>
            <CampoImagen
              uso="galeria"
              rotulo="Foto"
              ayuda="Una foto de frente, con buena luz."
              nombre={`foto-${n}`}
              nombreLegado={`foto-legado-${n}`}
              inicial={p.foto}
              legado={null}
            />
          </div>
        ))}
        {equipo.length < 8 && (
          <button
            type="button"
            className="boton"
            onClick={() => setEquipo([...equipo, { nombre: '' }])}
          >
            Agregar persona
          </button>
        )}
      </fieldset>

      <fieldset className="campo">
        <legend>Por qué confiar en ti</legend>
        {SENALES.map(([k, texto]) => (
          <label key={k} style={{ display: 'flex', gap: 8 }}>
            <input type="checkbox" name={`senal-${k}`} defaultChecked={i.senales?.includes(k)} />
            <span>{texto}</span>
          </label>
        ))}
        <label className="campo">
          <span>Tu garantía, en una línea</span>
          <input
            name="garantia"
            defaultValue={i.garantia ?? ''}
            maxLength={200}
            placeholder="Seis meses en motor y transmisión"
          />
        </label>
      </fieldset>

      <fieldset className="campo">
        <legend>Redes</legend>
        {REDES.map(([k, rotulo, ejemplo]) => (
          <label key={k} className="campo">
            <span>{rotulo}</span>
            <input
              name={`red-${k}`}
              type="url"
              defaultValue={i.redes?.[k] ?? ''}
              placeholder={ejemplo}
            />
          </label>
        ))}
      </fieldset>
      <button className="boton primario">Guardar identidad</button>
    </form>
  );
}
